import { useCreateReport } from '@/hooks/api/reports/useCreateReport';
import { Folder, MetadataFilterValue, ReportType } from '@/api/generatedApi';
import { FilterDataPopover } from '@/components/Search/SearchControls/SearchFiltersSection/FilterDataPopover/FilterDataPopover';
import { useTextSearch } from '@/hooks/api/crossTenant/textsearch/useTextSearch';
import { useGetFolders } from '@/hooks/useGetFolders';
import { useGetRootAssetCount } from '@/hooks/api/folders/useGetRootAssetCount';
import { buildFolderTree, flattenFolderTree, buildFolderPathToIdMap } from '@/utils/buildFolderTree';
import { usePreserveDataDuringLoading } from '@/components/FilterData/hooks/usePreserveDataDuringLoading';
import { BodyText } from '@/components/BodyText';
import { InputBox } from '@/components/Inputs/InputBox';
import { TextInput } from '@/components/Inputs/TextInput';
import { FilterSectionHeader } from '@/components/Search/SearchControls/SearchFiltersSection/FilterSectionHeader';
import { MetadataFilter } from '@/components/Search/SearchControls/SearchFiltersSection/MetadataFilter';
import { MatchRangeSlider } from '@/components/MatchRangeSlider';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Checkbox,
  TextField,
  Tooltip,
} from '@mui/material';
import { useSupportedFileTypes } from '@/hooks/useSupportedFileTypes';
import { useMemo, useState } from 'react';
import { REPORT_DESCRIPTIONS, REPORT_TITLES } from './consts';

// A folder name can never contain '/', so no folder path can ever equal this.
const ROOT_FOLDER_FILTER = '/';
const ROOT_FOLDER_LABEL = 'Root';

const getFolderScope = (folders: Folder[], selectedFolderIds: string[] | undefined) => {
  if (!selectedFolderIds) {
    return { folderIds: undefined, excludedFolderIds: undefined };
  }
  const selected = new Set(selectedFolderIds);
  const isSelected = (folderId?: string) => !!folderId && selected.has(folderId);
  const folderIds = folders
    .filter(folder => isSelected(folder.id) && !isSelected(folder.parentFolderId))
    .map(folder => folder.id);
  const excludedFolderIds = folders
    .filter(folder => !isSelected(folder.id) && isSelected(folder.parentFolderId))
    .map(folder => folder.id);
  return { folderIds, excludedFolderIds: excludedFolderIds.length > 0 ? excludedFolderIds : undefined };
};

const normalizeExtension = (extension: string): string => {
  return extension.startsWith('.') ? extension : `.${extension}`;
};

const getInitialThresholds = (reportType: ReportType): { min: number; max: number } => {
  if (reportType === ReportType.DUPLICATION) return { min: 100, max: 100 };
  if (reportType === ReportType.SIMPLIFICATION) return { min: 95, max: 100 };
  return { min: 80, max: 100 };
};

const formatThresholdDisplay = ({ min, max }: { min: number; max: number }): string => {
  if (min === max) {
    return `${min}%`;
  }

  return `${min}% to ${max}%`;
};

const getDefaultReportName = (reportType: ReportType): string => {
  const reportTitle = REPORT_TITLES[reportType].replace(' Report', '');
  const date = new Date().toLocaleDateString();
  return `${reportTitle} ${date}`;
};

const tryParseJsonArray = (value: string): string[] | undefined => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const toggleStringInArray = (array: string[], value: string): string[] => {
  return array.includes(value) ? array.filter(item => item !== value) : [...array, value];
};

export const CreateReportModal = ({
  isOpen,
  onClose,
  reportType,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  reportType: ReportType;
  onSuccess?: (reportId: string) => void;
}) => {
  const { createReport, isCreating } = useCreateReport();
  const { folders } = useGetFolders({ enabled: isOpen });
  const { rootAssetCount } = useGetRootAssetCount({ enabled: isOpen });
  const folderTree = buildFolderTree(folders);
  const foldersAsFilterData = flattenFolderTree(folderTree);
  const folderPathToIdMap = buildFolderPathToIdMap(folderTree);
  const rootFolderFilter = { filter: ROOT_FOLDER_FILTER, label: ROOT_FOLDER_LABEL, count: rootAssetCount };

  const initialThresholds = getInitialThresholds(reportType);
  const showEditableThresholds = reportType === ReportType.CUSTOM;
  const [name, setName] = useState('');
  const [minThreshold, setMinThreshold] = useState(initialThresholds.min);
  const [maxThreshold, setMaxThreshold] = useState(initialThresholds.max);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<Record<string, MetadataFilterValue>>({});
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [metadataFieldSearch, setMetadataFieldSearch] = useState<string>('');
  const [excludeAssemblies, setExcludeAssemblies] = useState(false);
  const [excludeExactDuplicates, setExcludeExactDuplicates] = useState(false);

  // The shared 3D list carries USD, so filtering off it directly offered .usd/.usdz to every
  // tenant. This is the same gate the upload picker reads.
  const { modelExtensions } = useSupportedFileTypes();

  const hasSelectedMetadataFilters = Object.keys(selectedMetadata).length > 0;
  const namedFolderSelections = selectedFolders.filter(f => f !== ROOT_FOLDER_FILTER);
  const textSearchEnabled =
    selectedFolders.length > 0 ||
    Object.keys(selectedMetadata).length > 0 ||
    searchQuery.length > 0 ||
    selectedExtensions.length > 0;

  const { data, isFetching: isFetchingFilters } = useTextSearch({
    searchQuery: searchQuery ?? '',
    selectedFilters: {
      extensions: selectedExtensions,
      labels: [],
      folders: namedFolderSelections,
      metadata: selectedMetadata,
    },
    enabled: textSearchEnabled,
  });

  const searchData = usePreserveDataDuringLoading(data, isFetchingFilters);

  const handleClearAllFolderFilters = () => {
    setSelectedFolders([]);
  };

  const handleClearAllExtensions = () => {
    setSelectedExtensions([]);
  };

  const handleClearAllMetadataFilters = () => {
    setSelectedMetadata({});
  };

  const handleFilterChange = (filterName: string, filterValue: string | number | boolean | undefined) => {
    if (filterName === 'Folders') {
      if (filterValue === undefined) {
        handleClearAllFolderFilters();
        return;
      }
      const valueString = String(filterValue);
      const parsedArray = tryParseJsonArray(valueString);
      setSelectedFolders(previous => parsedArray ?? toggleStringInArray(previous, valueString));
      return;
    }

    if (filterName === 'Extensions') {
      if (filterValue === undefined) {
        handleClearAllExtensions();
        return;
      }
      const valueString = String(filterValue);
      const parsedArray = tryParseJsonArray(valueString);
      setSelectedExtensions(previous => parsedArray ?? toggleStringInArray(previous, valueString));
      return;
    }

    const metadataFilterData = searchData?.filterData?.metadata?.[filterName] || [];
    const valueString = typeof filterValue === 'object' ? JSON.stringify(filterValue) : String(filterValue);
    const originalFilterData = metadataFilterData.find(
      item => (typeof item.filter === 'object' ? JSON.stringify(item.filter) : String(item.filter)) === valueString
    );
    const typedValue = originalFilterData ? originalFilterData.filter : filterValue;

    setSelectedMetadata(previous => {
      const currentValue = previous[filterName];
      const currentValueString = typeof currentValue === 'object' ? JSON.stringify(currentValue) : String(currentValue);

      if (typedValue === undefined || (currentValue !== undefined && currentValueString === valueString)) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [filterName]: _removed, ...rest } = previous;
        return rest;
      }

      return { ...previous, [filterName]: typedValue };
    });
  };

  const includeHomeFolderAssets = selectedFolders.includes(ROOT_FOLDER_FILTER);
  const selectedFolderIds =
    namedFolderSelections.length > 0
      ? namedFolderSelections.map(path => folderPathToIdMap.get(path)).filter((id): id is string => !!id)
      : undefined;

  const handleSubmit = () => {
    createReport(
      {
        type: reportType,
        name: name.trim() || getDefaultReportName(reportType),
        minThreshold,
        maxThreshold,
        searchQuery: searchQuery || undefined,
        metadataFilters: Object.keys(selectedMetadata).length > 0 ? selectedMetadata : undefined,
        ...getFolderScope(folders, selectedFolderIds),
        includeHomeFolderAssets: includeHomeFolderAssets || undefined,
        extensions:
          selectedExtensions.length > 0
            ? selectedExtensions.map(extension => normalizeExtension(extension))
            : undefined,
        excludeAssemblies: excludeAssemblies || undefined,
        excludeExactDuplicates: excludeExactDuplicates || undefined,
      },
      {
        onSuccess: response => {
          handleClose();
          onSuccess?.(response.data.report.id);
        },
      }
    );
  };

  const handleClose = () => {
    const resetThresholds = getInitialThresholds(reportType);
    setName('');
    setMinThreshold(resetThresholds.min);
    setMaxThreshold(resetThresholds.max);
    setSelectedFolders([]);
    setSelectedMetadata({});
    setSelectedExtensions([]);
    setSearchQuery('');
    setMetadataFieldSearch('');
    setExcludeAssemblies(false);
    setExcludeExactDuplicates(false);
    onClose();
  };

  const metadataFilterData = searchData?.filterData?.metadata ?? {};
  const hasMetadata = Object.keys(metadataFilterData).length > 0;
  const visibleMetadataEntries = useMemo(() => {
    const metadataSearchTerm = metadataFieldSearch.trim().toLowerCase();
    return Object.entries(metadataFilterData)
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([filterName]) => !metadataSearchTerm || filterName.toLowerCase().includes(metadataSearchTerm));
  }, [metadataFilterData, metadataFieldSearch]);

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>{`Create ${REPORT_TITLES[reportType]}`}</DialogTitle>
      <DialogContent>
        <TextField
          label='Report Name'
          value={name}
          onChange={event => setName(event.target.value)}
          placeholder={getDefaultReportName(reportType)}
          fullWidth
          margin='normal'
          helperText={`${REPORT_DESCRIPTIONS[reportType]}`}
        />
        <FilterSectionHeader title='Common Geometry' hasSelectedFilters={false} onClearAll={() => {}} />
        {showEditableThresholds ? (
          <InputBox>
            <Box marginTop='8px'>
              <MatchRangeSlider
                minThreshold={minThreshold}
                maxThreshold={maxThreshold}
                onMinThresholdChange={setMinThreshold}
                onMaxThresholdChange={setMaxThreshold}
              />
            </Box>
          </InputBox>
        ) : (
          <InputBox>
            <Box display='flex' alignItems='center' justifyContent='space-between' gap='12px' padding='10px 12px'>
              <BodyText color='text.secondary'>Match Threshold</BodyText>
              <BodyText color='text.primary' fontWeight={500}>
                {formatThresholdDisplay({ min: minThreshold, max: maxThreshold })}
              </BodyText>
            </Box>
          </InputBox>
        )}
        <Box sx={{ padding: '0 8px' }}>
          <Tooltip
            title='Excludes assets that are assemblies (files containing references to other parts) from the generated report groups.'
            placement='right'
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeAssemblies}
                  onChange={event => setExcludeAssemblies(event.target.checked)}
                  size='small'
                />
              }
              label='Exclude assemblies'
              slotProps={{
                typography: {
                  variant: 'body2',
                  color: 'text.secondary',
                },
              }}
            />
          </Tooltip>
          <Tooltip
            title='Excludes matches where two assets have 100% geometric similarity and the same file name, which typically indicates the same file uploaded more than once.'
            placement='right'
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeExactDuplicates}
                  onChange={event => setExcludeExactDuplicates(event.target.checked)}
                  size='small'
                />
              }
              label='Exclude exact duplicates'
              slotProps={{
                typography: {
                  variant: 'body2',
                  color: 'text.secondary',
                },
              }}
            />
          </Tooltip>
        </Box>
        <FilterSectionHeader
          title='Folders'
          hasSelectedFilters={selectedFolders.length > 0}
          onClearAll={handleClearAllFolderFilters}
        />
        <InputBox>
          <FilterDataPopover
            filterName='Folders'
            filterData={foldersAsFilterData}
            enableMultiSelect
            isDeferredApply
            useJsonPayload
            recursive
            cascadeToSubfolders
            onFilterChange={(_checkedCount, value) => handleFilterChange('Folders', value)}
            selectedFilterValues={selectedFolders}
            pinnedFilter={rootFolderFilter}
          />
        </InputBox>
        <FilterSectionHeader title='Text' hasSelectedFilters={false} onClearAll={() => {}} />
        <TextInput placeholder='All' value={searchQuery} onChange={event => setSearchQuery(event.target.value)} />
        <FilterSectionHeader
          title='Extensions'
          hasSelectedFilters={selectedExtensions.length > 0}
          onClearAll={handleClearAllExtensions}
        />
        <InputBox>
          <FilterDataPopover
            filterName='Extensions'
            filterData={modelExtensions
              .map(extension => extension.slice(1))
              .map(extension => ({
                filter: extension,
                label: extension,
                value: extension,
                count:
                  searchData?.filterData?.extensions?.find(
                    extensionDataPoint => extensionDataPoint.filter === extension
                  )?.count ?? 0,
              }))}
            enableMultiSelect
            isDeferredApply
            useJsonPayload
            hideZeroCounts
            onFilterChange={(_checkedCount, value) => handleFilterChange('Extensions', value)}
            selectedFilterValues={selectedExtensions}
          />
        </InputBox>
        <FilterSectionHeader
          title='Metadata'
          hasSelectedFilters={hasSelectedMetadataFilters}
          onClearAll={handleClearAllMetadataFilters}
        />
        {hasMetadata ? (
          <>
            {Object.keys(metadataFilterData).length > 5 && (
              <Box sx={{ marginBottom: '8px' }}>
                <TextInput
                  placeholder='Search metadata fields'
                  value={metadataFieldSearch}
                  onChange={event => setMetadataFieldSearch(event.target.value)}
                />
              </Box>
            )}
            {visibleMetadataEntries.length === 0 ? (
              <InputBox>
                <Box padding='10px 12px'>
                  <BodyText color='text.secondary'>No fields match your search.</BodyText>
                </Box>
              </InputBox>
            ) : (
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {visibleMetadataEntries.map(([filterName, filterDataPointMetadataValue], index, entries) => (
                  <Box key={`${filterName}-${index}`} data-testid={`metadata-filter-${index}`}>
                    <InputBox>
                      <MetadataFilter
                        filterName={filterName}
                        filterData={filterDataPointMetadataValue}
                        selectedFilters={{
                          folders: selectedFolders,
                          extensions: selectedExtensions,
                          labels: [],
                          metadata: selectedMetadata,
                        }}
                        onFilterChange={handleFilterChange}
                      />
                    </InputBox>
                    {index < entries.length - 1 && (
                      <Box sx={{ marginTop: '4px', padding: '0 8px 0 8px' }}>
                        <Divider />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Alert severity='warning'>
            {textSearchEnabled
              ? 'No metadata fields are present on assets matching the current filters.'
              : 'Apply a folder, text, or extension filter above to see the metadata fields available on matching assets.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={isCreating} sx={{ width: '100px' }}>
          {isCreating ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
